import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { CompetitionService } from '../../service/CompetitionService';
import LanguageService, { translatedMessage } from '../../service/LanguageService';
import { Dropdown } from 'primereact/dropdown';
import AnswerListComponent from './AnswerListComponent';
import SunEditor from 'suneditor-react';
import GeneralUtils from '../../utilities/GeneralUtils';
import ApiService from '../../service/ApiService';
import { Divider } from 'primereact/divider';
// import { math } from 'suneditor/src/plugins';
// import katex from 'katex';
import 'katex/dist/katex.min.css';
import { QuestionType } from './QuestionType';

let emptyQuestion = {
    name: null,
    id: null,
    questionType: '',
    questionContent: '',
    score: null,
    comment: '',
    answers: []
};

const QuestionEditDialog = (props) => {
    const [question, setQuestion] = useState(emptyQuestion);
    const [isNew, setIsNew] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const dialogToast = useRef(null);
    const toast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _question = props.value ? props.value : emptyQuestion;
        setQuestion(_question);
        setIsNew(!props.value?.id);
    }, [props.value, props.visible]);

    useEffect(() => {
        setDialogVisible(props.visible);
    }, [props.visible]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { ...question },
        validate: (data) => {
            let errors = {};

            if (!data.questionType) {
                errors.questionType = translatedMessage('form.error.questionType.required');
            }
            if (!data.questionContent) {
                errors.questionContent = translatedMessage('form.error.questionContent.required');
            }
            if (!data.score) {
                errors.score = translatedMessage('form.error.score.required');
            }
            if (data.questionType && data.questionType === 'FREE_TEXT' && !data.comment) {
                errors.comment = translatedMessage('form.error.correctionScale.required');
            }
            return errors;
        },
        onSubmit: (data) => {
            if (saveQuestion(data)) {
                formik.resetForm();
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const hideQuestionDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        setDialogVisible(false);
        formik.resetForm();
        refreshParent();
    };

    const refreshParent = () => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(question, false);
        }
    };

    const saveQuestion = (data) => {
        data.subjectProposal = { ...props.proposal, questions: null };
        competitionService
            .saveQuestion(data)
            .then((savedQuestion) => {
                dialogToast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                setQuestion(savedQuestion);
                if (!isNew || savedQuestion.questionType === 'FREE_TEXT') {
                    hideQuestionDialog();
                }
                setIsNew(!savedQuestion?.id);
                refreshParent();
                return true;
            })
            .catch((error) => {
                dialogToast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
                return false;
            });
    };

    const afterSaveQuestionAnswer = async (savedQuestionAnswer, isNew) => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(savedQuestionAnswer, isNew);
        }
    };

    const questionDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideQuestionDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="question-form" type="submit" />
        </>
    );

    window.addEventListener('error', (e) => {
        if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
            const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
            const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={dialogVisible} header={translatedMessage('competition.subjectProposal.question.details')} modal className="pcn-dialog" footer={questionDialogFooter} onHide={hideQuestionDialog}>
                <Toast ref={dialogToast} />
                <form id="question-form" onSubmit={formik.handleSubmit}>
                    <div className="p-inputgroup">
                        <span className="p-float-label">
                            <Dropdown
                                id="questionType"
                                name="questionType"
                                value={formik.values.questionType}
                                options={props.questionTypes}
                                onChange={(e) => {
                                    formik.setFieldValue('questionType', e.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldValid('questionType') })}
                                disabled={question.id}
                            />

                            <label htmlFor="questionType" className={classNames({ 'p-error': isFormFieldValid('questionType') })}>
                                {translatedMessage('competition.subjectProposal.question.type')}
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('questionType')}

                    <div className="pcn-sun-editor w-100 mt-3">
                        <label htmlFor="questionContent" className={classNames({ 'p-error': isFormFieldValid('questionContent') })}>
                            {translatedMessage('competition.subjectProposal.question.questionContent')}
                        </label>
                        <SunEditor
                            id="questionContent"
                            name="questionContent"
                            lang={LanguageService.getCurrentLanguage}
                            height="200px"
                            width="auto"
                            defaultValue={formik.values.questionContent}
                            placeholder={''}
                            setOptions={{
                                buttonList: GeneralUtils.getSunEditorButtonsWithImage(),
                                font: GeneralUtils.getSunEditorFonts(),
                                defaultStyle: GeneralUtils.getSunEditorDefaultStyle(),
                                charCounter: ApiService.getIsProduction() ? true : false,
                                maxCharCount: ApiService.getIsProduction() ? 10000 : null,
                                imageUploadSizeLimit: 5000000,
                                imageAccept: '.jpg,.jpeg,.png',
                                imageResizing: true,
                                imageHeightShow: true,
                                imageWidth: 'auto',
                                imageRotation: false,
                                imageFileInput: true,
                                imageUrlInput: false,
                                imageTabDisable: true
                                /*                                plugins: [math],
                                katex: katex*/
                            }}
                            onChange={(e) => formik.setFieldValue('questionContent', e)}
                        />
                        {getFormErrorMessage('questionContent')}
                    </div>

                    <div className="p-inputgroup mt-3">
                        <span className="p-float-label">
                            <InputNumber
                                id="score"
                                name="score"
                                value={formik.values.score}
                                onValueChange={formik.handleChange}
                                min={0}
                                max={100}
                                className={classNames({ 'p-invalid': isFormFieldValid('score') })}
                                useGrouping={false}
                                maxFractionDigits={2}
                            />
                            <label htmlFor="score" className={classNames({ 'p-error': isFormFieldValid('score') })}>
                                {translatedMessage('competition.subjectProposal.question.score')}*
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('score')}

                    <Divider />

                    {[QuestionType.FREE_TEXT, QuestionType.FILL_IN_THE_BLANKS].includes(formik.values.questionType) && (
                        <div className="field pcn-sun-editor w-100 mb-0">
                            <label htmlFor="comment" className={classNames({ 'p-error': isFormFieldValid('comment') })}>
                                {translatedMessage('competition.subjectProposal.question.comment')}
                            </label>
                            <SunEditor
                                id="comment"
                                name="comment"
                                lang={LanguageService.getCurrentLanguage}
                                height="200px"
                                width="auto"
                                defaultValue={formik.values.comment}
                                onValueChange={formik.handleChange}
                                placeholder={''}
                                setOptions={{
                                    buttonList: GeneralUtils.getSunEditorButtonsWithImage(),
                                    font: GeneralUtils.getSunEditorFonts(),
                                    defaultStyle: GeneralUtils.getSunEditorDefaultStyle(),
                                    charCounter: ApiService.getIsProduction() ? true : false,
                                    maxCharCount: ApiService.getIsProduction() ? 10000 : null,
                                    imageUploadSizeLimit: 5000000,
                                    imageAccept: '.jpg,.jpeg,.png',
                                    imageResizing: true,
                                    imageHeightShow: true,
                                    imageWidth: 'auto',
                                    imageRotation: false,
                                    imageFileInput: true,
                                    imageUrlInput: false,
                                    imageTabDisable: true
                                    /*                                plugins: [math],
                                                                    katex: katex*/
                                }}
                                onChange={(e) => formik.setFieldValue('comment', e)}
                            />

                            {getFormErrorMessage('comment')}
                        </div>
                    )}

                    {question.id && ![QuestionType.FREE_TEXT, QuestionType.FILL_IN_THE_BLANKS].includes(formik.values.questionType) && (
                        <>
                            <AnswerListComponent
                                answers={question.answers}
                                question={question}
                                afterSave={afterSaveQuestionAnswer}
                                questionTypes={props.questionTypes}
                                competition={props.competition}
                                permissions={props.permissions}
                            ></AnswerListComponent>
                        </>
                    )}
                </form>
            </Dialog>
        </>
    );
};

export default QuestionEditDialog;
